"use client";

import { useEffect, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { addFormHistory } from "@/lib/utils/crm/form-history";

interface UseFormAutoSaveOptions {
  entityName: string;
  debounceMs?: number;
  enabled?: boolean;
}

/**
 * Hook that auto-saves form data to history with debouncing
 * Watches form changes and persists them to localStorage
 */
export function useFormAutoSave<T extends Record<string, unknown>>(
  form: UseFormReturn<T>,
  options: UseFormAutoSaveOptions
) {
  const { entityName, debounceMs = 2000, enabled = true } = options;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<string>("");

  // Watch all form values
  const formData = form.watch();

  useEffect(() => {
    if (!enabled) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Create a signature of current form data to avoid duplicate saves
    const currentSignature = JSON.stringify(formData);

    // Only set timeout if data has changed
    if (currentSignature !== lastSavedRef.current) {
      timeoutRef.current = setTimeout(() => {
        try {
          addFormHistory({
            entityName,
            formData,
          });
          lastSavedRef.current = currentSignature;
        } catch (error) {
          console.warn("Failed to auto-save form history:", error);
        }
      }, debounceMs);
    }

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [formData, entityName, debounceMs, enabled]);
}
